import React, { useState } from 'react';
import { useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DSBox from '../../../design-system/DSBox';
import DSTypography from '../../../design-system/Typography/DSTypography';
import DSModal from '../../../design-system/DSModal';
import { ReactComponent as LeftIcon } from '../../../design-system/Icons/LeftIcon.svg';
import { ReactComponent as RightIcon } from '../../../design-system/Icons/RightIcon.svg';
import { GuideItem } from '../../../api/studio';
import LinkedText from './LinkedText';

const NOTE_BACKGROUND = 'rgba(255, 216, 72, 0.12)';

type GuideReaderModalProps = {
  guide: GuideItem;
  open: boolean;
  onClose: () => void;
};

const GuideReaderModal = ({ guide, open, onClose }: GuideReaderModalProps) => {
  const { palette, spacing } = useTheme();
  const [sectionIndex, setSectionIndex] = useState(0);

  const sections = guide.sections ?? [];
  const section = sections[sectionIndex] ?? sections[0];
  const isFirst = sectionIndex === 0;
  const isLast = sectionIndex === sections.length - 1;

  const handleClose = () => {
    onClose();
    setSectionIndex(0);
  };

  const handleNext = () => {
    if (isLast) {
      handleClose();
    } else {
      setSectionIndex((prev) => prev + 1);
    }
  };

  if (!section) {
    return null;
  }

  return (
    <DSModal open={open} onClose={handleClose}>
      <DSBox
        sx={{
          width: '86vw',
          height: '85vh',
          maxWidth: 1240,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <DSBox
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: spacing(2),
            padding: spacing(2.5, 3),
            borderBottom: `1px solid ${palette.white[8]}`,
          }}
        >
          <DSBox
            onClick={handleClose}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing(1),
              padding: spacing(1, 1.5),
              borderRadius: spacing(1.25),
              backgroundColor: palette.white[5],
              color: palette.text.secondary,
              cursor: 'pointer',
              '&:hover': { backgroundColor: palette.white[10] },
            }}
          >
            <LeftIcon width={16} height={16} />
            <DSTypography variant="body2" sx={{ color: 'inherit' }}>
              All guides
            </DSTypography>
          </DSBox>
          <DSBox sx={{ flex: 1, minWidth: 0 }}>
            <DSTypography
              variant="h6"
              sx={{
                fontFamily: 'Gilroy-Bold',
                color: palette.text.primary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {guide.title}
            </DSTypography>
            <DSTypography
              variant="body2"
              sx={{ color: palette.white[56], marginTop: spacing(0.5) }}
            >
              Guide · {sections.length} sections
            </DSTypography>
          </DSBox>
          <DSBox
            onClick={handleClose}
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              backgroundColor: palette.white[5],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: palette.text.secondary,
              flexShrink: 0,
              '&:hover': { backgroundColor: palette.white[10] },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </DSBox>
        </DSBox>

        <DSBox sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <DSBox
            sx={{
              width: 264,
              flexShrink: 0,
              borderRight: `1px solid ${palette.white[8]}`,
              padding: spacing(2.5, 2),
              overflowY: 'auto',
              display: { xs: 'none', md: 'block' },
            }}
          >
            <DSTypography
              variant="body2"
              sx={{
                color: palette.white[56],
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                fontSize: 11,
                padding: spacing(0, 1.25, 1.5),
              }}
            >
              Contents
            </DSTypography>
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing(0.5),
              }}
            >
              {sections.map((item, index) => {
                const active = index === sectionIndex;
                return (
                  <DSBox
                    key={index}
                    onClick={() => setSectionIndex(index)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing(1.5),
                      padding: spacing(1.25),
                      borderRadius: spacing(1.25),
                      cursor: 'pointer',
                      backgroundColor: active
                        ? palette.white[8]
                        : 'transparent',
                      '&:hover': { backgroundColor: palette.white[5] },
                    }}
                  >
                    <DSBox
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: spacing(1),
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: active
                          ? palette.primary.main
                          : palette.white[8],
                      }}
                    >
                      <DSTypography
                        sx={{
                          fontFamily: 'Gilroy-Bold',
                          fontSize: 11,
                          color: active
                            ? palette.primary.contrastText
                            : palette.white[56],
                        }}
                      >
                        {index + 1}
                      </DSTypography>
                    </DSBox>
                    <DSTypography
                      variant="body2"
                      sx={{
                        flex: 1,
                        fontFamily: active
                          ? 'Gilroy-SemiBold'
                          : 'Gilroy-Medium',
                        color: active
                          ? palette.text.primary
                          : palette.text.secondary,
                      }}
                    >
                      {item.heading}
                    </DSTypography>
                  </DSBox>
                );
              })}
            </DSBox>
          </DSBox>

          <DSBox
            key={sectionIndex}
            sx={{ flex: 1, minWidth: 0, overflowY: 'auto' }}
          >
            <DSBox
              sx={{
                maxWidth: 680,
                margin: '0 auto',
                padding: spacing(4, 5, 5),
              }}
            >
              <DSTypography
                variant="body2"
                sx={{
                  color: palette.primary.main,
                  letterSpacing: '.05em',
                  textTransform: 'uppercase',
                  fontSize: 12,
                  fontFamily: 'Gilroy-SemiBold',
                  marginBottom: spacing(1.5),
                }}
              >
                Section {sectionIndex + 1} of {sections.length}
              </DSTypography>
              <DSTypography
                variant="h5"
                sx={{
                  fontFamily: 'Gilroy-Bold',
                  color: palette.text.primary,
                  marginBottom: spacing(2.5),
                }}
              >
                {section.heading}
              </DSTypography>

              {(section.paragraphs ?? []).map((paragraph, index) => (
                <DSTypography
                  key={index}
                  variant="body1"
                  sx={{
                    color: palette.text.secondary,
                    marginBottom: spacing(2),
                    lineHeight: 1.7,
                  }}
                >
                  <LinkedText text={paragraph} links={section.links} />
                </DSTypography>
              ))}

              {(section.bullets ?? []).length > 0 && (
                <DSBox
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing(1.5),
                    margin: spacing(0.75, 0, 2.25),
                  }}
                >
                  {(section.bullets ?? []).map((bullet, index) => (
                    <DSBox
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: spacing(1.5),
                      }}
                    >
                      <DSBox
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          backgroundColor: palette.primary.main,
                          flexShrink: 0,
                          marginTop: spacing(1.125),
                        }}
                      />
                      <DSTypography
                        variant="body1"
                        sx={{
                          flex: 1,
                          color: palette.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {bullet.lead && (
                          <DSBox
                            component="b"
                            sx={{
                              color: palette.text.primary,
                              fontFamily: 'Gilroy-Bold',
                            }}
                          >
                            {bullet.lead}
                          </DSBox>
                        )}
                        <LinkedText text={bullet.text} links={section.links} />
                      </DSTypography>
                    </DSBox>
                  ))}
                </DSBox>
              )}

              {section.note && (
                <DSBox
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: spacing(1.5),
                    backgroundColor: NOTE_BACKGROUND,
                    borderRadius: spacing(1.75),
                    padding: spacing(2, 2.25),
                    marginTop: spacing(1),
                  }}
                >
                  <InfoOutlinedIcon
                    sx={{
                      color: palette.primary.main,
                      fontSize: 18,
                      flexShrink: 0,
                      marginTop: '1px',
                    }}
                  />
                  <DSTypography
                    variant="body2"
                    sx={{
                      flex: 1,
                      color: palette.text.primary,
                      lineHeight: 1.6,
                    }}
                  >
                    <LinkedText text={section.note} links={section.noteLinks} />
                  </DSTypography>
                </DSBox>
              )}

              <DSBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: spacing(2),
                  marginTop: spacing(4),
                  paddingTop: spacing(2.75),
                  borderTop: `1px solid ${palette.white[8]}`,
                }}
              >
                {!isFirst && (
                  <DSBox
                    onClick={() => setSectionIndex((prev) => prev - 1)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing(1),
                      maxWidth: '46%',
                      color: palette.text.secondary,
                      cursor: 'pointer',
                      '&:hover': { color: palette.text.primary },
                    }}
                  >
                    <LeftIcon width={16} height={16} />
                    <DSTypography
                      variant="body2"
                      sx={{ fontFamily: 'Gilroy-SemiBold', color: 'inherit' }}
                    >
                      {sections[sectionIndex - 1].heading}
                    </DSTypography>
                  </DSBox>
                )}
                <DSBox
                  onClick={handleNext}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing(1),
                    maxWidth: '46%',
                    marginLeft: 'auto',
                    color: isLast ? palette.primary.main : palette.text.primary,
                    cursor: 'pointer',
                  }}
                >
                  <DSTypography
                    variant="body2"
                    sx={{ fontFamily: 'Gilroy-SemiBold', color: 'inherit' }}
                  >
                    {isLast ? 'Done' : sections[sectionIndex + 1].heading}
                  </DSTypography>
                  <RightIcon width={16} height={16} />
                </DSBox>
              </DSBox>
            </DSBox>
          </DSBox>
        </DSBox>
      </DSBox>
    </DSModal>
  );
};

export default GuideReaderModal;
